/**
 * ExpiryLevelFilter Component - Filter by expiry level (L1-L3, A1-A3)
 */
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import { Clock, AlertTriangle, X, FileText, Building, Calendar } from 'lucide-react';
import { colors } from './constants';

export const ExpiryLevelFilterCard = ({
  reportResult,
  expirySettings,
  activeExpiryFilter,
  setActiveExpiryFilter
}) => {
  const { expiryL1, expiryL2, expiryL3, expiredA1, expiredA2 } = expirySettings;

  if (!reportResult?.expiry_settings) return null;

  return (
    <Card className="bg-white" style={{ borderColor: colors.border }}>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2" style={{ color: colors.dark }}>
          <Clock className="h-5 w-5" style={{ color: colors.blue }} />
          Ablauf-Stufen Filter
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Legend */}
        <div className="grid grid-cols-2 gap-4 p-3 rounded-lg text-xs" style={{ background: colors.bg }}>
          <div>
            <p className="font-medium mb-1" style={{ color: colors.dark }}>Läuft bald ab:</p>
            <p style={{ color: colors.gray }}>
              L1: {expiryL1} Wochen / L2: {expiryL2} Wochen / L3: {expiryL3} Wochen
            </p>
          </div>
          <div>
            <p className="font-medium mb-1" style={{ color: colors.dark }}>Abgelaufen:</p>
            <p style={{ color: colors.gray }}>
              A1: bis {expiredA1} Wochen / A2: {expiredA1}-{expiredA2} Wochen / A3: mehr als {expiredA2} Wochen
            </p>
          </div>
        </div>
        
        {/* Filter Buttons */}
        <div className="grid grid-cols-2 gap-4">
          {/* Expiring Soon - L1-L3 */}
          <div>
            <p className="text-sm font-medium mb-2" style={{ color: colors.dark }}>Läuft bald ab</p>
            <div className="flex gap-2 flex-wrap">
              {['L1', 'L2', 'L3'].map(level => (
                <Button
                  key={level}
                  size="sm"
                  variant={activeExpiryFilter === level ? 'default' : 'outline'}
                  onClick={() => setActiveExpiryFilter(activeExpiryFilter === level ? null : level)}
                  className={activeExpiryFilter === level ? 'bg-amber-500 hover:bg-amber-600' : ''}
                >
                  {level} ({reportResult.summary?.expiring_soon?.[level] || reportResult.summary?.expiring_soon_levels?.[level] || 0})
                </Button>
              ))}
            </div>
          </div>
          
          {/* Expired - A1-A3 */}
          <div>
            <p className="text-sm font-medium mb-2" style={{ color: colors.dark }}>Abgelaufen</p>
            <div className="flex gap-2 flex-wrap">
              {['A1', 'A2', 'A3'].map(level => (
                <Button
                  key={level}
                  size="sm"
                  variant={activeExpiryFilter === level ? 'default' : 'outline'}
                  onClick={() => setActiveExpiryFilter(activeExpiryFilter === level ? null : level)}
                  className={activeExpiryFilter === level ? 'bg-red-500 hover:bg-red-600' : ''}
                >
                  {level} ({reportResult.summary?.expired_levels?.[level] || 0})
                </Button>
              ))}
            </div>
          </div>
        </div>
        
        {/* Active Filter Indicator */}
        {activeExpiryFilter && (
          <ActiveFilterIndicator 
            activeExpiryFilter={activeExpiryFilter}
            expirySettings={expirySettings}
            onClear={() => setActiveExpiryFilter(null)}
          />
        )}
      </CardContent>
    </Card>
  );
};

const ActiveFilterIndicator = ({ activeExpiryFilter, expirySettings, onClear }) => {
  const { expiryL1, expiryL2, expiryL3, expiredA1, expiredA2 } = expirySettings;
  const isExpiring = activeExpiryFilter.startsWith('L');
  
  const getFilterText = () => {
    switch (activeExpiryFilter) {
      case 'L1': return `Läuft innerhalb ${expiryL1} Wochen ab`;
      case 'L2': return `Läuft innerhalb ${expiryL2} Wochen ab`;
      case 'L3': return `Läuft innerhalb ${expiryL3} Wochen ab`;
      case 'A1': return `Abgelaufen seit max. ${expiredA1} Wochen`;
      case 'A2': return `Abgelaufen seit ${expiredA1}-${expiredA2} Wochen`;
      case 'A3': return `Abgelaufen seit mehr als ${expiredA2} Wochen`;
      default: return '';
    }
  };

  return (
    <div 
      className="flex items-center justify-between p-2 rounded-lg border" 
      style={{ 
        borderColor: isExpiring ? '#f59e0b' : '#ef4444', 
        background: isExpiring ? '#fffbeb' : '#fef2f2' 
      }}
    >
      <span className="text-sm" style={{ color: isExpiring ? '#b45309' : '#b91c1c' }}>
        Filter aktiv: {activeExpiryFilter} - {getFilterText()}
      </span>
      <Button size="sm" variant="ghost" onClick={onClear} className="h-6 w-6 p-0">
        <X className="h-4 w-4" />
      </Button>
    </div>
  );
};

export const FilteredExpiryList = ({
  activeExpiryFilter,
  filteredByExpiryLevel,
  expirySettings,
  onUserClick,
  onClearFilter
}) => {
  const { expiryL1, expiryL2, expiryL3, expiredA1, expiredA2 } = expirySettings;
  const isExpiring = activeExpiryFilter?.startsWith('L');

  if (!activeExpiryFilter || !filteredByExpiryLevel) return null;

  const getTitle = () => {
    switch (activeExpiryFilter) {
      case 'L1': return `Läuft innerhalb ${expiryL1} Wochen ab`;
      case 'L2': return `Läuft innerhalb ${expiryL2} Wochen ab`;
      case 'L3': return `Läuft innerhalb ${expiryL3} Wochen ab`;
      case 'A1': return `Abgelaufen seit max. ${expiredA1} Wochen`;
      case 'A2': return `Abgelaufen seit ${expiredA1}-${expiredA2} Wochen`;
      case 'A3': return `Abgelaufen seit mehr als ${expiredA2} Wochen`;
      default: return '';
    }
  };

  return (
    <Card 
      className="bg-white md:col-span-2" 
      style={{ borderColor: isExpiring ? '#f59e0b' : '#ef4444' }}
    >
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between gap-4">
          <CardTitle className="text-base flex items-center gap-2" style={{ color: colors.dark }}>
            {isExpiring ? (
              <Clock className="h-5 w-5 text-amber-500" />
            ) : (
              <AlertTriangle className="h-5 w-5 text-red-500" />
            )}
            {getTitle()}
            <Badge variant="outline">{filteredByExpiryLevel.length} Einträge</Badge>
          </CardTitle>
          <Button size="sm" variant="ghost" onClick={onClearFilter}>
            <X className="h-4 w-4 mr-1" />
            Filter zurücksetzen
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <ScrollArea className="h-[400px]">
          <div className="space-y-2">
            {filteredByExpiryLevel.map((item, idx) => (
              <div 
                key={idx} 
                className="p-3 rounded-lg border bg-white hover:bg-gray-50 transition-colors"
                style={{ borderColor: isExpiring ? '#fcd34d' : '#fca5a5' }}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <p 
                      className="font-medium cursor-pointer hover:underline"
                      style={{ color: colors.dark }}
                      onClick={() => onUserClick(item)}
                    >
                      {item.user_name || 'Unbekannt'}
                    </p>
                    <div className="flex items-center gap-4 text-xs mt-1" style={{ color: colors.gray }}>
                      {item.course_name && (
                        <span className="flex items-center gap-1">
                          <FileText className="h-3 w-3" />
                          {item.course_name}
                        </span>
                      )}
                      {item.team && (
                        <span className="flex items-center gap-1">
                          <Building className="h-3 w-3" />
                          {item.team}
                        </span>
                      )}
                      {item.expiry_date && (
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {item.expiry_date}
                        </span>
                      )}
                    </div>
                  </div>
                  <Badge className={isExpiring ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-800'}>
                    {activeExpiryFilter}
                  </Badge>
                </div>
              </div>
            ))}
            {filteredByExpiryLevel.length === 0 && (
              <div className="text-center py-8" style={{ color: colors.gray }}>
                Keine Einträge in dieser Kategorie gefunden
              </div>
            )}
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  );
};
